/* eslint-disable @typescript-eslint/no-explicit-any */
import { EditableComponent, ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { AEMReactCompMap, respGridUtil, updateAEMCQKeys } from '@marriott/mi-ui-library';
import { CobrandTabs } from '../../molecules/CobrandTabs';
import { StyledCobrandTabContainer } from './CobrandTabContainer.styles';
import clsx from 'clsx';
import { useStore } from '../../modules/store/cobrandStore';
import React, { FC, ReactElement, useEffect, useState } from 'react';
import { TabProps, TabContainerProps, TabDetails } from './CobrandTabContainer.types';
import { CobrandComparisonTray } from '../CobrandComparisonTray/CobrandComparisonTray';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';

export const TabContainerConfig = {
  emptyLabel: 'CobrandTabContainer',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/cobrandtabcontainer`,
};

export const TabContainerComp: FC<TabProps> = (props: TabProps) => {
  const {
    header,
    headerTag,
    subHeader,
    trackingProperties,
    styleclass,
    componentId,
    pagePath,
    itemPath,
    isAuthorMode,
    contentBlockAlignment,
    headerFontSize,
    subFontSize,
    subHeaderTag,
    descriptionText,
    descFontSize,
    tabDetails,
    layoutContainer,
    resolvedUrl,
    enableCardComparisonFeature,
    showCheckbox,
    targetExperience,
  } = props;

  const updatedProps = updateAEMCQKeys(props);
  const cqItems = updatedProps['cqItems'];
  const { cobrandData } = useStore(store => store);
  const mapper = AEMReactCompMap(props?.allowedComponents);
  const [isEditMode, setEditMode] = useState(false);
  const ENABLE_COMPARE_CHECK_BOX = useClientEnvVarsStore.getState()?.envVarsObject?.['ENABLE_COMPARE_CHECK_BOX'];

  useEffect(() => {
    const pageWcmMode = document.getElementsByTagName('html')[0];
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'class') {
          const currWcmMode = pageWcmMode?.getAttribute('class');
          if (currWcmMode?.includes('Edit')) {
            setEditMode(true);
          } else if (currWcmMode?.includes('Preview')) {
            setEditMode(false);
          }
        }
      });
    });
    observer.observe(pageWcmMode, { attributes: true });
    return () => {
      observer.disconnect();
    };
  }, []);
  const getItemsSorted = (card: any) => {
    const Items = Object.entries(card[':items']);
    const itemsOrder = card[':itemsOrder'];
    return Object.fromEntries(Items.sort((a: any, b: any) => itemsOrder.indexOf(a[0]) - itemsOrder.indexOf(b[0])));
  };
  // This function is used to render the component in authoring mode authorComponentWrapper
  const authorComponentWrapper = (index: number) => {
    return (
      <ResponsiveGrid
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        pagePath={pagePath}
        itemPath={`${itemPath}/${tabDetails[index].nodeName}`}
        columnCount="12"
        gridClassNames={''}
        customClassName={''}
        config={{
          isEmpty: () => true,
          resourceType: 'mi-aem-common-spa/components/container',
        }}
      />
    );
  };

  // This function is used to render the component in end-user mode
  const publishComponentWrapper = (jsonData: any, idx: number): ReactElement | null => {
    delete jsonData.tabDetails;
    // eslint-disable-next-line no-prototype-builtins
    if (jsonData?.hasOwnProperty(`${tabDetails[idx].nodeName}`)) {
      const page = jsonData[`${tabDetails[idx].nodeName}`];
      const pageItems: any = getItemsSorted(page);
      const returnable: any[] = [];
      for (const itemKey in pageItems) {
        if (Object.prototype.hasOwnProperty.call(pageItems, itemKey)) {
          const item = pageItems[itemKey];
          const itemType = item[':type']?.split('/')?.pop();
          if (Object.prototype.hasOwnProperty.call(mapper, itemType)) {
            const itemProps = {
              ...item,
              componentPath: props?.componentPath,
              resolvedUrl: resolvedUrl,
              enableCardComparisonFeature: enableCardComparisonFeature,
              enable2CardLayout: showCheckbox,
              showCheckbox: showCheckbox,
              styleClass: ENABLE_COMPARE_CHECK_BOX && itemKey.includes('cta_component') ? 'd-none' : '',
              targetExperience,
            };
            const reqJsonData = { ...page };
            reqJsonData[':items'] = { [itemKey]: itemProps };
            returnable.push(
              <ResponsiveGrid model={respGridUtil(reqJsonData)} {...respGridUtil(reqJsonData)} key={itemKey} />
            );
          }
        }
      }
      return <div className={clsx('col-12 no-list-style')}>{returnable}</div>;
    }
    return null;
  };

  return (
    <StyledCobrandTabContainer data-testid="cobrandtabcontainer" data-component-name="o-cobrand-cobrandtabcontainer">
      <div className={clsx(`${componentId}-container py-5 pb-md-0 `, styleclass, 't-background-color')}>
        <CobrandTabs
          componentId={componentId}
          subHeader={subHeader}
          trackingProperties={trackingProperties}
          header={header}
          headerTag={headerTag}
          isAuthorMode={isAuthorMode}
          contentBlockAlignment={contentBlockAlignment}
          headerFontSize={headerFontSize}
          subFontSize={subFontSize}
          subHeaderTag={subHeaderTag}
          descriptionText={descriptionText}
          descFontSize={descFontSize}
          tabDetails={tabDetails}
          layoutContainer={layoutContainer}
          isEditMode={isEditMode}
          componentPath={props?.componentPath}
        >
          {isAuthorMode && Array.from({ length: tabDetails?.length }, (_, i) => authorComponentWrapper(i))}
          {!isAuthorMode && tabDetails?.map((_tab: TabDetails, idx) => publishComponentWrapper(cqItems, idx))}
        </CobrandTabs>
      </div>
      {cobrandData.selectedCards && <CobrandComparisonTray {...props} />}
    </StyledCobrandTabContainer>
  );
};

export const TabEditableComponent = (props: any) => {
  const { model } = props;
  const cqType = model?.cqType || model?.[':type'];
  return (
    <EditableComponent config={TabContainerConfig} {...props}>
      <TabContainerComp {...props} componentName={cqType?.split('/')?.pop()} />
    </EditableComponent>
  );
};

export const CobrandTabContainer: FC<TabContainerProps> = React.memo((props: TabContainerProps) => {
  return (
    <div className={clsx(props?.model?.styleclass)}>
      <TabEditableComponent {...props} />
    </div>
  );
});
